/**
 * Least-squares curve fitting for pump characterisation.
 *
 * Fits student-entered (Q, H) data to the pump curve model:
 *
 *   H = a − b·Q²
 *
 * Substituting x = Q², this is standard linear regression:
 *   y = a + c·x     where c = −b
 */

/**
 * Fit  H = a − b·Q²  to an array of data points.
 *
 * @param {Array<{Q: number, H: number}>} data  – at least 2 points
 * @returns {{ a: number, b: number, R2: number } | null}
 */
export function fitPumpCurve(data) {
  if (!data || data.length < 2) return null;

  const n = data.length;
  const { Sx, Sy, Sxy, Sx2 } = data.reduce(
    (acc, pt) => {
      const x = pt.Q * pt.Q; // Q²
      acc.Sx += x;
      acc.Sy += pt.H;
      acc.Sxy += x * pt.H;
      acc.Sx2 += x * x; // Q⁴
      return acc;
    },
    { Sx: 0, Sy: 0, Sxy: 0, Sx2: 0 }
  );

  const denom = n * Sx2 - Sx * Sx;
  if (Math.abs(denom) < 1e-30) return null;

  const a = (Sy * Sx2 - Sx * Sxy) / denom;
  const c = (n * Sxy - Sx * Sy) / denom;
  const b = -c; // H = a − bQ²  →  c = −b

  // Coefficient of determination R²
  const yMean = Sy / n;
  const { SSres, SStot } = data.reduce(
    (acc, pt) => {
      const yPred = a + c * pt.Q * pt.Q;
      acc.SSres += (pt.H - yPred) ** 2;
      acc.SStot += (pt.H - yMean) ** 2;
      return acc;
    },
    { SSres: 0, SStot: 0 }
  );

  const R2 = SStot > 0 ? 1 - SSres / SStot : 1;

  return { a, b, R2, n };
}
